import React, { useState } from "react";
import useFetch from "@/lib/useFetch";
import { GlassCard, PageTitle, StatusBadge, EmptyState } from "@/components/lux";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { LifeBuoy, Send, CheckCircle } from "lucide-react";

const inp = "w-full rounded-xl border border-white/10 bg-white/5 px-4 py-3 text-sm text-white outline-none focus:border-[var(--gold)]/50";
const STATUSES = ["all", "open", "answered", "closed"];
const CATLABEL = { deposit: "Deposit", withdrawal: "Withdrawal", fund: "Fund Regarding", profile: "Profile Change", other: "Other" };
const CATS = ["all", "deposit", "withdrawal", "fund", "profile", "other"];

export default function AdminSupport() {
  const [status, setStatus] = useState("all");
  const [cat, setCat] = useState("all");
  const { data, loading, reload } = useFetch(`/admin/support/tickets?status=${status}&category=${cat}`, [status, cat]);
  const [openId, setOpenId] = useState(null);
  const [reply, setReply] = useState("");

  const tickets = data?.tickets || [];

  const sendReply = async (id) => {
    if (!reply.trim()) return;
    try { await api.post(`/admin/support/tickets/${id}/reply`, { message: reply }); setReply(""); reload(); toast.success("Reply sent"); }
    catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };
  const approveProfile = async (id) => {
    try { await api.post(`/admin/support/tickets/${id}/approve-profile-change`); reload(); toast.success("Profile change approved and applied"); }
    catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };

  const setTicketStatus = async (id, st) => {
    try { await api.post(`/admin/support/tickets/${id}/status`, { status: st }); reload(); toast.success(`Marked ${st}`); }
    catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };

  return (
    <div className="space-y-6">
      <PageTitle title="Support Tickets" subtitle={`${data?.open_count ?? 0} open ticket(s)`} />
      <div className="flex flex-wrap gap-2">
        {STATUSES.map((s) => (
          <button key={s} onClick={() => setStatus(s)} className={`btn3d ${status === s ? "btn3d-gold" : "btn3d-dark"} px-3 py-1.5 text-xs capitalize`} data-testid={`support-filter-${s}`}>{s}</button>
        ))}
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-xs text-[var(--text-dim)]">Category:</span>
        {CATS.map((c) => (
          <button key={c} onClick={() => setCat(c)} className={`btn3d ${cat === c ? "btn3d-gold" : "btn3d-dark"} px-3 py-1.5 text-xs`} data-testid={`support-cat-${c}`}>{c === "all" ? "All" : CATLABEL[c]}</button>
        ))}
      </div>
      <GlassCard className="p-6">
        {loading ? <div className="skeleton h-40" /> : tickets.length ? (
          <div className="space-y-3">
            {tickets.map((t) => (
              <div key={t.ticket_id} className="rounded-xl glass-2 p-4" data-testid={`admin-ticket-${t.ticket_id}`}>
                <button onClick={() => setOpenId(openId === t.ticket_id ? null : t.ticket_id)} className="flex w-full items-center justify-between text-left" data-testid={`admin-ticket-toggle-${t.ticket_id}`}>
                  <div>
                    <p className="text-sm font-semibold text-white">{t.subject}</p>
                    <p className="text-xs text-[var(--text-dim)]">{t.ticket_id} · <span className="text-[var(--gold)]">{CATLABEL[t.category] || "Other"}</span> · {t.member_id} · {t.name} · {(t.updated_at || "").slice(0, 10)}</p>
                  </div>
                  <StatusBadge status={t.status} />
                </button>
                {openId === t.ticket_id && (
                  <div className="mt-3 space-y-2 border-t border-white/10 pt-3">
                    {t.messages.map((m, i) => (
                      <div key={i} className={`rounded-lg p-2.5 text-xs ${m.sender === "admin" ? "border border-[var(--gold)]/20 bg-[var(--gold)]/10" : "bg-white/5"}`}>
                        <p className="mb-1 font-semibold text-white">{m.sender === "admin" ? (m.sender_name || "Support") : t.name} <span className="font-normal text-[var(--text-dim)]">· {(m.created_at || "").slice(0, 16).replace("T", " ")}</span></p>
                        <p className="whitespace-pre-wrap text-[var(--text-dim)]">{m.body}</p>
                      </div>
                    ))}
                    <div className="flex gap-2 pt-1">
                      <input className={inp + " flex-1"} placeholder="Type a reply..." value={reply} onChange={(e) => setReply(e.target.value)} data-testid={`admin-reply-input-${t.ticket_id}`} />
                      <button onClick={() => sendReply(t.ticket_id)} className="grid place-items-center rounded-xl btn3d-gold px-3 text-black" data-testid={`admin-reply-send-${t.ticket_id}`}><Send size={16} /></button>
                    </div>
                    {t.profile_change_request && t.status !== "closed" && (
                      <button onClick={() => approveProfile(t.ticket_id)} className="btn3d btn3d-gold px-3 py-1.5 text-xs text-black" data-testid={`admin-approve-profile-${t.ticket_id}`}><CheckCircle size={13} className="mr-1 inline" /> Approve Profile Change</button>
                    )}
                    <div className="flex gap-2">
                      {t.status !== "closed"
                        ? <button onClick={() => setTicketStatus(t.ticket_id, "closed")} className="btn3d btn3d-dark px-3 py-1.5 text-xs" data-testid={`admin-close-${t.ticket_id}`}>Close Ticket</button>
                        : <button onClick={() => setTicketStatus(t.ticket_id, "open")} className="btn3d btn3d-dark px-3 py-1.5 text-xs" data-testid={`admin-reopen-${t.ticket_id}`}>Reopen</button>}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        ) : <EmptyState icon={LifeBuoy} title="No tickets" />}
      </GlassCard>
    </div>
  );
}
